// FILE: src/features/resourceSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

// Fetch resources (optional query param 'type')
export const fetchAdminResources = createAsyncThunk(
  "resources/fetchAdminResources",
  async (type) => {
    // e.g. /api/resources?type=CHAPTER
    const url = type
      ? `/api/admin/resources?type=${encodeURIComponent(type)}`
      : "/api/admin/resources";

    return await fetchWithAuth(url);
  }
);
export const fetchStudentResources = createAsyncThunk(
  "resources/fetchStudentResources",
  async (type) => {
    // e.g. /api/resources?type=CHAPTER
    const url = type
      ? `/api/resources?type=${encodeURIComponent(type)}`
      : "/api/resources";

    return await fetchWithAuth(url);
  }
);

// One resource by id (admin edit page; works after a reload, B-13).
export const fetchAdminResource = createAsyncThunk("resources/fetchAdminResource", async (id) => {
  return await fetchWithAuth(`/api/admin/resources/${encodeURIComponent(id)}`);
});

// Update resource (PATCH)
export const updateResource = createAsyncThunk(
  "resources/updateResource",
  async ({ id, updates }) => {
    const url = `/api/admin/resources/${id}`;
    return await fetchWithAuth(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates)
    });
  }
);

const resourceSlice = createSlice({
  name: "resources",
  initialState: {
    list: [],
    chapters: [],
    homelinks: [],
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchResources
      .addCase(fetchAdminResources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminResources.fulfilled, (state, action) => {
        state.loading = false;
        state.list = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAdminResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchStudentResources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentResources.fulfilled, (state, action) => {
        state.loading = false;

        if (action.meta.arg === "CHAPTER") {
          state.chapters = action.payload;
        } else if (action.meta.arg === "HOMELINK") {
          state.homelinks = action.payload;
        }
      })
      .addCase(fetchStudentResources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAdminResource.fulfilled, (state, action) => {
        const item = action.payload;
        if (!item?._id) return;
        const idx = state.list.findIndex((r) => r._id === item._id);
        if (idx !== -1) state.list[idx] = item;
        else state.list.push(item);
      })
      // updateResource. A refused save is shown by the edit page itself; it
      // must not set the shared `error`, which used to hide the form until
      // a reload (B-13).
      .addCase(updateResource.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated?._id) return;
        const idx = state.list.findIndex((r) => r._id === updated._id);
        if (idx !== -1) {
          state.list[idx] = updated;
        }
      });
  }
});

export default resourceSlice.reducer;
